import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";

const SERVICES = [
  {
    id: "digital-acquisition",
    n: "01",
    title: "Digital Acquisition Funnels & SEO",
    tagline: "High-velocity pipeline growth for European SMEs & tech distributors",
    description:
      "Custom performance marketing infrastructure, cross-border paid campaigns, and organic search content strategies that systematically scale B2B pipelines by 30%+.",
    img: "https://images.unsplash.com/photo-1470075801209-17f9ec0cada6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxmdXR1cmlzdGljJTIwY29ycG9yYXRlJTIwYnVpbGRpbmclMjBhcmNoaXRlY3R1cmUlMjBtb2Rlcm4lMjBnbGFzcyUyMGR1c2t8ZW58MHx8fHwxNzkxNTU1NzM1fDA&ixlib=rb-4.1.0&q=85",
    deliverables: [
      "Multi-channel paid acquisition (LinkedIn, Google Search, programmatic)",
      "Technical & content SEO architecture for European cross-border markets",
      "Conversion rate optimization (CRO) & high-intent landing page systems",
    ],
  },
  {
    id: "ai-crm-automation",
    n: "02",
    title: "AI Automation & Salesforce CRM",
    tagline: "Intelligent lead scoring & 22% faster lead-to-opportunity conversion",
    description:
      "Integration of cutting-edge AI copilots, automated lead qualification, and end-to-end Salesforce CRM architecture to lower CAC and eliminate friction points.",
    img: "https://images.unsplash.com/photo-1748609160056-7b95f30041f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxleGVjdXRpdmUlMjBkaWdpdGFsJTIwYWR2aXNvcnklMjBjb25zdWx0YW5jeSUyMGRhdGElMjBhbmFseXRpY3MlMjB3b3Jrc3BhY2V8ZW58MHx8fHwxNzkxNTU1NzQyfDA&ixlib=rb-4.1.0&q=85",
    deliverables: [
      "Salesforce CRM custom object mapping & pipeline orchestration",
      "AI lead enrichment & predictive intent scoring",
      "Automated multi-touch nurture sequences & deal acceleration rules",
    ],
  },
  {
    id: "commercial-market-assessment",
    n: "03",
    title: "Commercial Market Assessment",
    tagline: "Enterprise strategy, supply chain optimization & buying behavior analysis",
    description:
      "Rigorous commercial market evaluations for enterprise brands like FIAT Automobile, examining supply chain bottlenecks, digital buyer journeys, and multichannel retail enablement.",
    img: "https://images.unsplash.com/photo-1765475886628-a7c1fa36b0af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxmaWF0JTIwYXV0b21vYmlsZSUyMGNhciUyMHNob3dyb29tJTIwbW9kZXJuJTIwRXVyb2BlYW4lMjB2ZWhpY2xlfGVufDB8fHx8MTc5MTU1NTczNXww&ixlib=rb-4.1.0&q=85",
    deliverables: [
      "Multichannel automotive & retail workflow diagnostics",
      "Digital buying behavior & friction mapping",
      "Go-to-market strategy & supply chain integration playbooks",
    ],
  },
];

const PROCESS = [
  { n: "01", t: "Diagnose", d: "Audit your funnel, CRM and market data to find where pipeline leaks." },
  { n: "02", t: "Architect", d: "Design the acquisition, automation and measurement system end to end." },
  { n: "03", t: "Automate", d: "Ship AI scoring, routing and nurture into Salesforce and your stack." },
  { n: "04", t: "Scale", d: "Compound with CRO loops, SEO assets and cross-border expansion." },
];

export default function Services() {
  return (
    <>
      <SEO
        title="Services — Vector Growth and Advisory"
        description="Digital acquisition funnels & SEO, AI automation & Salesforce CRM, and commercial market assessments for European enterprises."
      />
      <section className="relative overflow-hidden pt-40 pb-20 md:pt-48" data-testid="services-hero">
        <div className="spot pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-10">
          <Eyebrow>Capabilities</Eyebrow>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl" data-testid="services-headline">
            Three systems. <span className="text-gradient">One vector.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-500">
            Every engagement combines acquisition, automation and intelligence — engineered as one
            system, pointed at pipeline.
          </p>
        </div>
      </section>

      {SERVICES.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className={`scroll-mt-28 border-t hairline ${i % 2 === 1 ? "bg-surface/30" : ""}`}
          data-testid={`service-section-${s.id}`}
        >
          <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-6 py-20 md:px-10 md:py-28 lg:grid-cols-2">
            <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
              <p className="font-mono text-6xl font-semibold text-azure/25">{s.n}</p>
              <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{s.title}</h2>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon/80">{s.tagline}</p>
              <p className="mt-6 text-base leading-relaxed text-slate-600">{s.description}</p>
              <ul className="mt-8 space-y-3">
                {s.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm text-slate-600" data-testid={`service-deliverable-${s.id}`}>
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-neon" /> {d}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid={`service-cta-${s.id}`}
                className="group mt-10 inline-flex items-center gap-2 rounded-full border border-neon/40 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-neon transition-all duration-300 hover:bg-neon hover:text-white"
              >
                Discuss this system
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
            <Reveal delay={0.12} className={i % 2 === 1 ? "lg:order-1" : ""}>
              <div className="clip-frame overflow-hidden border hairline">
                <img src={s.img} alt={s.title} className="h-[380px] w-full object-cover transition-transform duration-700 hover:scale-105" />
              </div>
            </Reveal>
          </div>
        </section>
      ))}

      <section className="border-t hairline" data-testid="process-section">
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10">
          <Reveal>
            <Eyebrow>How we work</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
              From diagnosis to compounding scale.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1} className="h-full">
                <div className="h-full rounded-2xl border hairline bg-surface/70 p-7 transition-colors duration-300 hover:border-neon/40">
                  <p className="font-mono text-xs tracking-[0.3em] text-neon/70">{p.n}</p>
                  <h3 className="mt-5 font-display text-xl font-bold text-ink">{p.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
